import type { ReactNode } from "react";
import Link from "next/link";
import Image from "next/image";
import { useTranslations, useLocale } from "next-intl";

function FooterLogo({
  size = 16,
  homeHref = "/",
}: {
  size?: number;
  homeHref?: string;
}) {
  return (
    <Link
      href={homeHref}
      aria-label="TrySchedule Home"
      className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
    >
      <Image
        src="/icon.svg"
        alt="TrySchedule - Free Online Schedule Builder"
        width={size}
        height={size}
        className="object-contain"
      />
      <span className="text-blue-600 dark:text-blue-400">
        <span className="font-bold">Try</span>
        <span className="font-normal">Schedule</span>
      </span>
    </Link>
  );
}

function FooterTagline({ children }: { children: ReactNode }) {
  return (
    <p className="text-xs text-gray-500 dark:text-gray-500 italic text-center">
      {children}
    </p>
  );
}

type FooterLink = {
  href: string;
  label: string;
};

export function Footer() {
  const t = useTranslations("Common");
  const locale = useLocale();

  // Generate locale-aware URLs. The bare root is special-cased so the Spanish
  // home stays "/es" instead of "/es/" (mirrors buildLocalizedUrl in lib/seo.ts).
  const getLocalizedUrl = (path: string) => {
    if (locale === "en") return path;
    return path === "/" ? `/${locale}` : `/${locale}${path}`;
  };

  // Template links
  const templateLinks = [
    {
      href: "/templates/study-schedule-builder",
      label: t("nav.studyScheduleBuilder"),
    },
    {
      href: "/templates/employee-schedule-builder",
      label: t("nav.employeeSchedule"),
    },
    {
      href: "/templates/ai-schedule-builder",
      label: t("nav.aiScheduleBuilder"),
    },
  ];

  // Blog links
  const blogLinks = [
    {
      href: "/blog/how-to-create-a-work-schedule",
      label: t("nav.blogWorkSchedule"),
    },
    {
      href: "/blog/schedule-builder-vs-excel",
      label: t("nav.blogScheduleBuilderVsExcel"),
    },
    {
      href: "/blog/how-to-make-a-weekly-work-schedule",
      label: t("nav.blogWeeklyWorkSchedule"),
    },
  ];

  // Company links
  const companyLinks = [
    { href: "/pricing", label: t("nav.pricing") },
    { href: "/about", label: t("footer.about") },
    { href: "/terms", label: t("footer.terms") },
    { href: "/privacy", label: t("footer.privacy") },
    { href: "/contact", label: t("footer.contact") },
  ];

  const taglineContent = t.rich("footer.tagline", {
    brand: (chunks) => (
      <strong className="font-medium text-gray-700 dark:text-gray-400">
        {chunks}
      </strong>
    ),
    highlight: (chunks) => (
      <strong className="font-medium text-gray-700 dark:text-gray-400">
        {chunks}
      </strong>
    ),
  });

  const LinkItem = ({
    href,
    label,
    className = "block text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors",
  }: FooterLink & { className?: string }) => (
    <Link
      href={getLocalizedUrl(href)}
      className={className}
    >
      {label}
    </Link>
  );

  return (
    <footer className="border-t border-gray-200 bg-white py-6 dark:border-gray-800 dark:bg-gray-950 md:py-8">
      <div className="container mx-auto px-4 text-sm">
        <div className="flex justify-center">
          <nav
            aria-label={t("footer.navLabel")}
            className="grid w-full max-w-5xl gap-4 md:grid-cols-3 md:gap-6"
          >
            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-900 dark:text-gray-100 md:text-sm md:normal-case md:tracking-normal">
                {t("nav.categoryTemplates")}
              </h3>
              <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-gray-600 dark:text-gray-400 md:flex-col md:gap-2">
                {templateLinks.map((link) => (
                  <LinkItem
                    key={link.href}
                    href={link.href}
                    label={link.label}
                    className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 md:block"
                  />
                ))}
              </div>
            </section>

            <div className="grid grid-cols-2 gap-4 md:contents">
              <section>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-900 dark:text-gray-100 md:text-sm md:normal-case md:tracking-normal">
                  {t("nav.categoryBlog")}
                </h3>
                <div className="flex flex-col gap-0.5 text-gray-600 dark:text-gray-400 md:gap-2">
                  {blogLinks.map((link) => (
                    <LinkItem key={link.href} href={link.href} label={link.label} />
                  ))}
                </div>
              </section>

              <section>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-900 dark:text-gray-100 md:text-sm md:normal-case md:tracking-normal">
                  {t("nav.categoryCompany")}
                </h3>
                <div className="flex flex-col gap-0.5 text-gray-600 dark:text-gray-400 md:gap-2">
                  {companyLinks.map((link) => (
                    <LinkItem key={link.href} href={link.href} label={link.label} />
                  ))}
                </div>
              </section>
            </div>
          </nav>
        </div>

        <div className="mt-8 flex w-full flex-col items-center gap-1 border-t border-gray-200 pt-4 dark:border-gray-800">
          <div className="flex flex-wrap items-center justify-center gap-1.5 md:gap-2">
            <FooterLogo size={16} homeHref={getLocalizedUrl("/")} />
            <span className="text-xs text-gray-500 dark:text-gray-500 md:text-sm md:text-gray-600">
              {"\u00A9"} 2025 TrySchedule. {t("footer.rights")}
            </span>
          </div>
          <FooterTagline>{taglineContent}</FooterTagline>
        </div>
      </div>
    </footer>
  );
}
